import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import rehypeRaw from "rehype-raw"
import { cn } from "@/lib/utils"

const markdownComponents: Components = {
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  ),
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 last:mb-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 last:mb-0">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  h1: ({ children }) => (
    <h1 className="mb-2 mt-3 text-base font-semibold first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-3 text-base font-semibold first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h3>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-border pl-3 text-muted-foreground last:mb-0">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-border" />,
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="w-full border-collapse text-left text-xs">
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-border bg-muted/50 px-2 py-1 font-medium">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-border px-2 py-1">{children}</td>
  ),
  code: ({ className, children, ...props }) => {
    const isFenced = Boolean(className)
    if (isFenced) {
      return (
        <code
          className={cn(
            "font-mono text-xs break-words whitespace-pre-wrap",
            className
          )}
          {...props}
        >
          {children}
        </code>
      )
    }
    return (
      <code
        className="rounded bg-background/70 px-1 py-0.5 font-mono text-[0.9em] break-words dark:bg-background/40"
        {...props}
      >
        {children}
      </code>
    )
  },
  // Code blocks wrap and grow DOWN (whitespace-pre-wrap + break-words) instead of forcing the
  // chat bubble past the window; overflow-x-auto stays as a fallback for anything unbreakable.
  pre: ({ children }) => (
    <pre className="mb-2 max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-border bg-background/80 p-3 text-xs last:mb-0 dark:bg-background/40">
      {children}
    </pre>
  ),
  // ST-14 deep-dive debug: collapsible <details> boxes (only present when allowHtml + RAG_DEEP_DIVE_DEBUG).
  details: ({ children }) => (
    <details className="mb-2 rounded-md border border-border bg-background/50 px-3 py-2 last:mb-0">
      {children}
    </details>
  ),
  summary: ({ children }) => (
    <summary className="cursor-pointer select-none font-medium text-muted-foreground">
      {children}
    </summary>
  ),
}

interface ChatMessageContentProps {
  content: string
  role: "user" | "assistant"
  /**
   * Allow raw HTML in the markdown (via rehype-raw). Used ONLY for the deep-dive bubble so the ST-14
   * smoke-debug <details> collapsibles render. Off everywhere else (answers stay HTML-safe).
   */
  allowHtml?: boolean
}

export function ChatMessageContent({ content, role, allowHtml = false }: ChatMessageContentProps) {
  if (role === "user") {
    return (
      <p className="text-sm whitespace-pre-wrap">{content}</p>
    )
  }

  return (
    <div className="chat-markdown min-w-0 max-w-full text-sm leading-relaxed break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={allowHtml ? [rehypeRaw] : []}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
